import React, { useState, useRef, useEffect } from 'react';
import styles from './TypeaheadDropdown.module.css';
import IconArrowFilled from "@/components/icons/IconArrowFilled";
import Flyout from '@/components/Flyout';

interface TypeaheadSelectProps {
    options: string[];
    optionRender?: (option: string, index: number) => React.ReactNode;
    onChange?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    id?: string;
    value?: string;
    valueToLabelMap?: Record<string, string>;
}

const TypeaheadDropdown = <T,>({
   options,
   optionRender,
   onChange,
   placeholder = "Select options",
   disabled = false,
   id,
   value,
   valueToLabelMap
}: TypeaheadSelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value);
    const [filteredOptions, setFilteredOptions] = useState<string[]>(options);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const _optionRender = optionRender ?? ((option: string): React.ReactNode => option.toString());

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
                inputRef.current && !inputRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        setInputValue(value);
        setSelectedOption(value);
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        setFilteredOptions(
            options.filter((option) =>
                option.toString().toLowerCase().includes(value.toLowerCase())
            )
        );
        setIsOpen(true);
        if (onChange) {
            onChange(value);
        }
    };

    const handleOptionSelect = (option: string) => {
        setSelectedOption(option);
        const newInputValue = valueToLabelMap ? valueToLabelMap[option] : option;
        setInputValue(newInputValue);
        setIsOpen(false);
        if (onChange) {
            onChange(option.toString());
        }
    };

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setFilteredOptions(options);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.inputWrapper}>
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onClick={() => setIsOpen(true)}
                    className={styles.input}
                    placeholder={placeholder}
                    disabled={disabled}
                    id={id}
                />
                <button
                    onClick={toggleDropdown}
                    className={styles.toggleButton}
                    disabled={disabled}
                >
                    <IconArrowFilled />
                </button>
            </div>
            {isOpen && (
                <Flyout ref={dropdownRef} className={styles.dropdownList}>
                    <ul>
                        {filteredOptions.map((option, index) => (
                            <li
                                key={index}
                                onClick={() => handleOptionSelect(option)}
                                className={`${styles.option} ${option === selectedOption ? styles.selectedOption : ''}`}
                            >
                                {_optionRender(option, index)}
                                {option === selectedOption && (
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <g id="check">
                                            <path id="Vector" d="M6.36667 11.7167C6.27778 11.7167 6.19444 11.7027 6.11667 11.6747C6.03889 11.6471 5.96667 11.6 5.9 11.5333L3.03333 8.66666C2.91111 8.54444 2.85267 8.386 2.858 8.19133C2.86378 7.99711 2.92778 7.83888 3.05 7.71666C3.17222 7.59444 3.32778 7.53333 3.51667 7.53333C3.70556 7.53333 3.86111 7.59444 3.98333 7.71666L6.36667 10.1L12.0167 4.45C12.1389 4.32777 12.2973 4.26666 12.492 4.26666C12.6862 4.26666 12.8444 4.32777 12.9667 4.45C13.0889 4.57222 13.15 4.73044 13.15 4.92466C13.15 5.11933 13.0889 5.27777 12.9667 5.4L6.83333 11.5333C6.76667 11.6 6.69444 11.6471 6.61667 11.6747C6.53889 11.7027 6.45556 11.7167 6.36667 11.7167Z" fill="#FF5823"/>
                                        </g>
                                    </svg>
                                )}
                            </li>
                        ))}
                    </ul>
                </Flyout>
            )}
        </div>
    );
};

export default TypeaheadDropdown;